import React, { useCallback, useEffect, useRef } from 'react';

import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import { bind } from 'bind-event-listener';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';

type ContentSizeMessagePayload = {
	height?: number;
	type?: string;
	uniqueKey?: string;
	width?: number;
};

type PreviewPanelSnifferProps = {
	analyticsAttributes?: AnalyticsAttributes;
	analyticsClient?: AnalyticsWebClient | undefined | null;
	children: React.ReactNode;
	containerRef: React.RefObject<HTMLDivElement | null>;
	panelType?: string;
	parentProduct?: string;
};

/**
 * Preview Panel Sniffer is a helper component that is able to detect bad UX smells associated with Preview Panels.
 * By communicating with the consumer, it's able to tell when certain things, like overflows, occur and report them for investigation.
 */
export const PreviewPanelSniffer = ({
	children,
	containerRef,
	analyticsClient,
	parentProduct,
	panelType,
	analyticsAttributes,
}: PreviewPanelSnifferProps): React.JSX.Element => {
	const hasSentOverflowEventRef = useRef(false);

	const isValidContentSizeMessage = useCallback(
		(
			event: MessageEvent,
			contentSizePayload: ContentSizeMessagePayload,
		): contentSizePayload is ContentSizeMessagePayload & {
			height: number;
			type: 'contentSize';
			width: number;
		} => {
			if (
				contentSizePayload.type !== 'contentSize' ||
				typeof contentSizePayload.width !== 'number' ||
				contentSizePayload.width <= 0 ||
				typeof contentSizePayload.height !== 'number' ||
				contentSizePayload.height <= 0
			) {
				return false;
			}

			const panelIframe = containerRef.current?.querySelector('iframe');
			const panelIframeWindow = panelIframe?.contentWindow;
			if (!panelIframeWindow || event.source !== panelIframeWindow) {
				return false;
			}

			// If both origins are known, ensure the message comes from the iframe's origin.
			if (event.origin && event.origin !== 'null' && panelIframe?.src) {
				try {
					const iframeOrigin = new URL(panelIframe.src, window.location.href).origin;
					if (event.origin !== iframeOrigin) {
						return false;
					}
				} catch {
					return false;
				}
			}

			return true;
		},
		[containerRef],
	);

	const sendOverflowEvent = useCallback(
		({
			clientWidth,
			detectionMethod,
			scrollWidth,
		}: {
			clientWidth?: number;
			detectionMethod: 'body-observer' | 'iframe-content-size-message';
			scrollWidth?: number;
		}) => {
			if (!analyticsClient || hasSentOverflowEventRef.current) {
				return;
			}

			hasSentOverflowEventRef.current = true;
			const overflowPx =
				typeof clientWidth === 'number' && typeof scrollWidth === 'number'
					? Math.max(scrollWidth - clientWidth, 0)
					: undefined;

			analyticsClient.sendOperationalEvent({
				action: 'detected',
				actionSubject: 'layoutOverflow',
				actionSubjectId: 'preview-panel-horizontal-overflow',
				source: 'preview-panel',
				attributes: {
					parentProduct,
					panelType,
					detectionMethod,
					clientWidth,
					scrollWidth,
					overflowPx,
					// Availability check only — this decides whether to decorate the overflow
					// operational event with analyticsAttributes, NOT whether the user is
					// accessing the Capacity planning experience. We therefore read the GA
					// experiment WITHOUT firing a Statsig exposure
					// (fireExperimentExposure: false); exposure is fired at the point the user
					// actually opens the Capacity experience, keeping the GA experiment's
					// population clean.
					//
					// The OR still short-circuits: when the legacy EAP gate is ON the experiment
					// is never read at all. EAP customers remain unconditionally enabled.
					...(fg('add_capacity_tab_to_the_board_horizontal_nav') ||
					FeatureGates.getExperimentValue('individual_capacity_planning_ga', 'isEnabled', false, {
						fireExperimentExposure: false,
					})
						? analyticsAttributes
						: {}),
				},
			});
		},
		[analyticsClient, parentProduct, panelType, analyticsAttributes],
	);

	useEffect(() => {
		const bodyElement = containerRef.current;
		if (!bodyElement || typeof ResizeObserver === 'undefined') {
			return;
		}

		const detectBodyOverflow = () => {
			const clientWidth = bodyElement.clientWidth;
			const scrollWidth = bodyElement.scrollWidth;
			if (scrollWidth > clientWidth) {
				sendOverflowEvent({
					detectionMethod: 'body-observer',
					clientWidth,
					scrollWidth,
				});
			}
		};

		detectBodyOverflow();
		const observer = new ResizeObserver(detectBodyOverflow);
		observer.observe(bodyElement);

		return () => {
			observer.disconnect();
		};
	}, [containerRef, sendOverflowEvent]);

	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			const contentSizePayload = event.data as ContentSizeMessagePayload;
			if (!isValidContentSizeMessage(event, contentSizePayload)) {
				return;
			}

			const panelIframe = containerRef.current?.querySelector('iframe');
			const iframeClientWidth = panelIframe?.clientWidth ?? 0;
			const clientWidth =
				iframeClientWidth > 0 ? iframeClientWidth : containerRef.current?.clientWidth;
			if (typeof clientWidth !== 'number' || contentSizePayload.width <= clientWidth) {
				return;
			}

			sendOverflowEvent({
				detectionMethod: 'iframe-content-size-message',
				clientWidth,
				scrollWidth: contentSizePayload.width,
			});
		};

		return bind(window, { type: 'message', listener: handleMessage });
	}, [containerRef, isValidContentSizeMessage, sendOverflowEvent]);

	return <>{children}</>;
};
